import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Quote } from '../../types/quote';
import QuoteTimer from './QuoteTimer';
import { Bank } from '../../services/api/bankService';
import { quoteService } from '../../services/api/quoteService';
import { logger } from '../../utils/logger';
import '../../styles/MultiQuoteModal.css';

interface MultiQuoteModalProps {
  isOpen: boolean;
  tradeData: {
    clientTaxId: string;
    currency1: string;
    currency2: string;
    direction: 'Buy' | 'Sell';
    amount: number;
    currency1SettlementDate: string;
    currency2SettlementDate: string;
    product: 'Spot' | 'Forward';
  };
  banks: Bank[];  // Pass in the banks array from parent
  onQuoteSelected: (quote: Quote) => void;
  onCancel: () => void;
}

interface QuoteWithBank extends Quote {
  bankName: string;
  isLoading?: boolean;
}

const MultiQuoteModal: React.FC<MultiQuoteModalProps> = ({
  isOpen,
  tradeData,
  banks,
  onQuoteSelected,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState<QuoteWithBank[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasRequestedQuote = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset the flag when modal closes
      hasRequestedQuote.current = false;
      return;
    }

    // Only request quote once when modal opens
    if (hasRequestedQuote.current) return;
    hasRequestedQuote.current = true;

    // Reset state when modal opens
    setLoadingQuotes(true);
    setQuotes([]);
    setError(null);

    // Request quotes from all three banks (separately for staggered arrival)
    const requestQuote = async () => {
      try {
        logger.info('Requesting quotes from backend API', tradeData);

        const bankIds = ['banco-abc', 'banco-internacional', 'banco-bci'];

        // Request quotes from each bank separately so they arrive progressively
        const quotePromises = bankIds.map(async (bankId) => {
          try {
            const quotesResponse = await quoteService.requestQuote({
              clientTaxId: tradeData.clientTaxId,
              currency1: tradeData.currency1,
              currency2: tradeData.currency2,
              direction: tradeData.direction,
              amount: tradeData.amount,
              currency1SettlementDate: tradeData.currency1SettlementDate,
              currency2SettlementDate: tradeData.currency2SettlementDate,
              product: tradeData.product,
              counterparties: [bankId], // Request from single bank
            });

            // Get the quote and add bank name
            const quote = quotesResponse[0];
            const bank = banks.find(b => b.id === quote.counterparty);
            if (!bank) {
              throw new Error(`Bank not found for counterparty: ${quote.counterparty}`);
            }

            const quoteWithBankName = {
              ...quote,
              bankName: bank.name,
            };

            // Add quote as it arrives (progressive display)
            setQuotes(prev => [...prev, quoteWithBankName]);
            logger.info(`Quote received from ${bank.name}`);

            return quoteWithBankName;
          } catch (err) {
            logger.error(`Failed to get quote from ${bankId}`, err);
            return null;
          }
        });

        // Wait for all quotes to complete
        await Promise.all(quotePromises);

        logger.info('All quotes received');

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to request quote';
        logger.error('Failed to request quote', err);
        setError(errorMessage);
      } finally {
        setLoadingQuotes(false);
      }
    };

    requestQuote();

    // No cleanup needed since we're using async/await instead of timers
  }, [isOpen, tradeData.clientTaxId, tradeData.currency1, tradeData.currency2, tradeData.direction, tradeData.amount, tradeData.currency1SettlementDate, tradeData.currency2SettlementDate, tradeData.product, banks]);

  if (!isOpen) return null;

  // Find the best (lowest) price for Buy, or highest for Sell
  const bestPrice = tradeData.direction === 'Buy'
    ? Math.min(...quotes.map(q => q.quotedPrice))
    : Math.max(...quotes.map(q => q.quotedPrice));

  const handleSelectQuote = (quote: QuoteWithBank) => {
    onQuoteSelected(quote);
  };

  const handleQuoteExpired = (quoteId: string) => {
    // Remove expired quote from list
    setQuotes(prev => prev.filter(q => q.quoteId !== quoteId));
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-dialog quote-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('tradeBooking.quote.modalTitle', 'Request Quotes')}</h2>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Trade Summary */}
          <div className="quote-trade-summary">
            <div className="summary-item">
              <span className="summary-label">{t('tradeBooking.tradeType.label', 'Product Type')}:</span>
              <span className="summary-value">{tradeData.product === 'Spot' ? t('tradeBooking.tradeType.spot', 'Spot') : t('tradeBooking.tradeType.forward', 'Forward')}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('tradeBooking.tradeDetails.direction', 'Direction')}:</span>
              <span className="summary-value">{tradeData.direction} {tradeData.amount.toLocaleString()} {tradeData.currency1}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('tradeBooking.tradeDetails.pair', 'Currency Pair')}:</span>
              <span className="summary-value">{tradeData.currency1}/{tradeData.currency2}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('tradeBooking.quote.valueDate', 'Value Date')}:</span>
              <span className="summary-value">{tradeData.currency1SettlementDate}</span>
            </div>
          </div>

          {/* Error State */}
          {error && !loadingQuotes && (
            <div className="quotes-error">
              <p className="error-message">{error}</p>
              <p className="error-help">{t('tradeBooking.quote.errorHelp', 'Please try again or contact support if the problem persists.')}</p>
            </div>
          )}

          {/* Loading State */}
          {loadingQuotes && quotes.length === 0 && !error && (
            <div className="quotes-loading">
              <div className="spinner"></div>
              <p>{t('tradeBooking.quote.requestingQuotes', 'Requesting quotes from banks...')}</p>
            </div>
          )}

          {/* Quotes List */}
          {quotes.length > 0 && (
            <div className="quotes-list">
              <div className="quotes-header">
                <span>{t('tradeBooking.quote.bank', 'Bank')}</span>
                <span>{t('tradeBooking.quote.price', 'Price')}</span>
                <span>{t('tradeBooking.quote.expires', 'Expires')}</span>
                <span></span>
              </div>

              {quotes.map((quote) => {
                const isBestPrice = quote.quotedPrice === bestPrice;

                return (
                  <div
                    key={quote.quoteId}
                    className={`quote-row ${isBestPrice ? 'best-price' : ''}`}
                  >
                    <div className="quote-bank">
                      {quote.bankName}
                      {isBestPrice && (
                        <span className="best-price-badge">
                          {t('tradeBooking.quote.bestPrice', 'Best')}
                        </span>
                      )}
                    </div>

                    <div className="quote-price">
                      {quote.quotedPrice.toFixed(2)}
                    </div>

                    <div className="quote-timer">
                      <QuoteTimer
                        dueTimestamp={quote.dueTimestamp}
                        onExpired={() => handleQuoteExpired(quote.quoteId)}
                      />
                    </div>

                    <div className="quote-action">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleSelectQuote(quote)}
                      >
                        {t('tradeBooking.quote.select', 'Select')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Still Loading Indicator */}
          {loadingQuotes && quotes.length > 0 && (
            <div className="quotes-loading-more">
              <div className="spinner-small"></div>
              <span>{t('tradeBooking.quote.waitingForMore', 'Waiting for more quotes...')}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>
            {t('tradeBooking.actions.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiQuoteModal;
